import type {
  Speciality,
  SpecialistDoctor,
} from '@/lib/specialities/deriveSpeciality';

import {
  cleanSpeciality,
  sortSpecialitiesByTitle,
  getDoctorMainSpeciality,
  getDoctorSubSpecialities,
} from '@/lib/specialities/deriveSpeciality';

export type { Speciality, SpecialistDoctor };
export {
  cleanSpeciality,
  sortSpecialitiesByTitle,
  getDoctorMainSpeciality,
  getDoctorSubSpecialities,
};

type SearchParamsObject = Record<string, string | string[] | undefined | null>;

type SearchParamsLike = {
  get: (name: string) => string | null;
};

export type FilterSearchParams =
  URLSearchParams | SearchParamsLike | SearchParamsObject | null | undefined;

export const getDoctorAllSpecialities = (
  doctor: SpecialistDoctor,
): Speciality[] => {
  const unique = new Map<string, Speciality>();

  const mainSpeciality = getDoctorMainSpeciality(doctor);

  if (mainSpeciality) {
    unique.set(mainSpeciality.slug, mainSpeciality);
  }

  doctor.specialities
    ?.map(cleanSpeciality)
    .filter((speciality): speciality is Speciality => Boolean(speciality))
    .forEach((speciality) => {
      unique.set(speciality.slug, speciality);
    });

  return Array.from(unique.values());
};

const hasGetMethod = (value: unknown): value is SearchParamsLike => {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (value as SearchParamsLike).get === 'function'
  );
};

const getParam = (params: FilterSearchParams, key: string): string => {
  if (!params) return '';

  if (hasGetMethod(params)) {
    return params.get(key) ?? '';
  }

  const value = params[key];

  if (Array.isArray(value)) {
    return value[0] ?? '';
  }

  return value ?? '';
};

const getParamList = (params: FilterSearchParams, key: string): string[] => {
  return getParam(params, key)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
};

const normalizeText = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.toLowerCase().trim();
};

const getDoctorGender = (doctor: SpecialistDoctor): string => {
  if (typeof doctor.gender === 'string') return doctor.gender;
  return doctor.gender?.title ?? '';
};

const matchesSurgeonType = (doctor: SpecialistDoctor): boolean => {
  const description = normalizeText(doctor.description);
  const type = normalizeText(doctor.type);

  const typeOfDoctorMatches =
    doctor.typeOfDoctor?.some((item) => {
      const title = normalizeText(item.title);
      const slug = normalizeText(item.slug);

      return title.includes('surgeon') || slug.includes('surgeon');
    }) ?? false;

  return (
    type === 'surgeon' || description.includes('surgeon') || typeOfDoctorMatches
  );
};

const matchesConsultantType = (doctor: SpecialistDoctor): boolean => {
  return normalizeText(doctor.description).includes('consultant');
};

const matchesPhysiotherapistType = (doctor: SpecialistDoctor): boolean => {
  const description = normalizeText(doctor.description);
  const primary = normalizeText(
    typeof doctor.primarySpeciality === 'string'
      ? doctor.primarySpeciality
      : (doctor.primarySpeciality?.title ?? ''),
  );
  return description.includes('physiotherapist') || primary === 'physiotherapy';
};

const matchesRadiologistType = (doctor: SpecialistDoctor): boolean => {
  const description = normalizeText(doctor.description);
  const primary = normalizeText(
    typeof doctor.primarySpeciality === 'string'
      ? doctor.primarySpeciality
      : (doctor.primarySpeciality?.title ?? ''),
  );
  return description.includes('radiologist') || primary.includes('radiology');
};

const matchesAnaesthetistType = (doctor: SpecialistDoctor): boolean => {
  const description = normalizeText(doctor.description);
  const primary = normalizeText(
    typeof doctor.primarySpeciality === 'string'
      ? doctor.primarySpeciality
      : (doctor.primarySpeciality?.title ?? ''),
  );
  return description.includes('anaesthetist') || primary === 'anaesthetics';
};

const matchesDoctorType = (doctor: SpecialistDoctor, type: string): boolean => {
  switch (type) {
    case 'surgeon':
      return matchesSurgeonType(doctor);
    case 'consultant':
      return matchesConsultantType(doctor);
    case 'physiotherapist':
      return matchesPhysiotherapistType(doctor);
    case 'radiologist':
      return matchesRadiologistType(doctor);
    case 'anaesthetist':
      return matchesAnaesthetistType(doctor);
    default:
      return true;
  }
};

export const filterDoctorsBySearchParams = <T extends SpecialistDoctor>(
  doctors: T[],
  params: FilterSearchParams,
): T[] => {
  const searchQuery = normalizeText(getParam(params, 'q'));
  const selectedSpecialities = getParamList(params, 'speciality');
  const selectedSubSpecialities = getParamList(params, 'subSpeciality');
  const selectedGender = getParam(params, 'gender');
  const selectedType = getParam(params, 'type');
  const mainOnly = getParam(params, 'mainOnly') === 'true';

  return doctors.filter((doctor) => {
    const mainSpeciality = getDoctorMainSpeciality(doctor);
    const allSpecialities = getDoctorAllSpecialities(doctor);
    const allSpecialitySlugs = allSpecialities.map(
      (speciality) => speciality.slug,
    );
    const subSpecialitySlugs =
      doctor.specialities
        ?.map(cleanSpeciality)
        .filter((speciality): speciality is Speciality => Boolean(speciality))
        .map((speciality) => speciality.slug) ?? [];

    const searchableText = [
      doctor.fullName,
      doctor.heading,
      doctor.description,
      mainSpeciality?.title,
      ...allSpecialities.map((speciality) => speciality.title),
    ]
      .map(normalizeText)
      .join(' ');

    const matchesSearch = !searchQuery || searchableText.includes(searchQuery);

    const matchesSpeciality =
      selectedSpecialities.length === 0 ||
      selectedSpecialities.some((slug) => {
        if (mainOnly) {
          return mainSpeciality?.slug === slug;
        }

        return (
          mainSpeciality?.slug === slug || allSpecialitySlugs.includes(slug)
        );
      });

    const matchesSubSpeciality =
      selectedSubSpecialities.length === 0 ||
      selectedSubSpecialities.some((slug) => subSpecialitySlugs.includes(slug));

    const matchesGender =
      !selectedGender || getDoctorGender(doctor) === selectedGender;

    const matchesType =
      !selectedType || matchesDoctorType(doctor, selectedType);

    return (
      matchesSearch &&
      matchesSpeciality &&
      matchesSubSpeciality &&
      matchesGender &&
      matchesType
    );
  });
};
